import React, { useMemo } from "react";
import { MultiSelect, type Option } from "./MultiSelect";
import type { MultiValue } from "react-select";

interface EntitySelectorProps {
    data: (string | { id: string; name: string })[];
    onSelect: (value: string[], index: number) => void;
    index: number;
    value?: string | string[];
    label: string;
    description?: string;
    allLabel: string;
    placeholder?: string;
    valueKey?: 'id' | 'name';
    maxItems?: number;
    multiSelect?: boolean;
}

/** Converts an item to an option for the select */
const toOption = (item: string | { id: string; name: string }, allLabel: string, valueKey: 'id' | 'name'): Option => {
    if (item === "*") {
        return { value: "*", label: allLabel };
    }

    if (typeof item === "string") {
        return { value: item, label: item };
    }

    const value = valueKey === 'id' ? item.id : item.name;
    return { value: value, label: item.name };
};

const normalizeValue = (value?: string | string[]): string[] => {
    if (!value) {
        return [];
    }

    return Array.isArray(value) ? value : [value];
};

/**
 * Handles special logic for "*" option (All entities)
 */
const handleStarSelection = (newValues: string[], currentValues: string[]): string[] => {
    const hasStar = newValues.includes("*");
    const hadStar = currentValues.includes("*");

    // Select "*" -> keep only "*"
    if (hasStar && !hadStar) {
        return ["*"];
    }

    // "*" was selected and selecting something else -> remove "*"
    if (hasStar && hadStar) {
        return newValues.filter(v => v !== "*");
    }

    // Normal selection
    return newValues;
};

const EntitySelector: React.FC<EntitySelectorProps> = React.memo((
    {
        data,
        onSelect,
        index,
        value,
        label,
        description,
        allLabel,
        placeholder,
        valueKey = 'name',
        maxItems,
        multiSelect
    }) => {

    const effMultiSelect = multiSelect !== false;

    const options = useMemo(
        () => [...(allLabel ? ["*"] : []), ...data].map(item => toOption(item, allLabel, valueKey)),
        [data, allLabel, valueKey]
    );

    const currentValues = normalizeValue(value);

    const selectedOptions = useMemo(
        () => options.filter(opt => currentValues.includes(opt.value)),
        [options, currentValues]
    );

    const handleChangeMulti = (newValue: MultiValue<Option>) => {

        const newValues = newValue?.map(opt => opt.value) || [];
        let finalValues = handleStarSelection(newValues, currentValues);

        if (maxItems && !finalValues.includes("*") && finalValues.length > maxItems) {
            finalValues = finalValues.slice(-maxItems);
        }

        onSelect(finalValues, index);
    };
    const handleChangeSingle = (newValue: Option) => {
        // const newValues = newValue?.map(opt => opt.value) || [];
        // let finalValues = handleStarSelection(newValues, currentValues);

        // if (maxItems && !finalValues.includes("*") && finalValues.length > maxItems) {
        //     finalValues = finalValues.slice(-maxItems);
        // }
        onSelect([newValue.value], index);
    };
    const handleChange = (effMultiSelect ? handleChangeMulti:handleChangeSingle) as typeof handleChangeMulti;

    const isMaxReached = maxItems ? !currentValues.includes("*") && currentValues.length >= maxItems : false;

    return (
        <div className="flex flex-col gap-1 w-full">
            {description ? (
                <label className="text-sm font-medium group relative cursor-help w-fit">
                    {label}
                    <span
                        className="invisible group-hover:visible absolute z-50 bg-black/85 text-white rounded px-3 py-2 text-sm leading-relaxed pointer-events-none whitespace-normal max-w-xs left-0 bottom-full mb-1 w-max">
                        {description}
                    </span>
                </label>
            ) : (
                <label className="text-sm font-medium">{label}</label>
            )}

            <MultiSelect
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                placeholder={placeholder || `Choisissez ${label.toLowerCase()}`}
                closeMenuOnSelect={!!maxItems}
                isOptionDisabled={(option) => isMaxReached && !currentValues.includes(option.value) && option.value !== "*"}
                multiSelect={effMultiSelect}
            />
        </div>
    );
});

export default EntitySelector;
